<template>
  <div
    class="detail-info-item"
    :class="{
      'detail-info-item--summary': summary,
      'detail-info-item--inline': inline,
      'detail-info-item--editable': editable,
      'detail-info-item--wide': wide,
    }">
    <div class="detail-info-item__label-wrap">
      <span class="detail-info-item__label">{{ label }}</span>
      <slot name="label-action" />
    </div>

    <div class="detail-info-item__value">
      <slot>
        <StatusChip
          v-if="type === 'status' && !isEmptyValue"
          :label="value"
          :tone="value"
          size="small"
          icon
        />
        <span
          v-else-if="type === 'link' && !isEmptyValue"
          class="detail-info-item__link"
          @click="$emit('click')">
          {{ value }}
        </span>
        <EmptyValueChip v-else-if="isEmptyValue" value="" />
        <span v-else>{{ value }}</span>
      </slot>
    </div>

    <div v-if="$slots.action" class="detail-info-item__action">
      <slot name="action" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import EmptyValueChip from '@/components/common/EmptyValueChip.vue'
import StatusChip from '@/components/common/StatusChip.vue'

export default defineComponent({
  name: 'DetailInfoItem',
  components: {
    EmptyValueChip,
    StatusChip,
  },
  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: null,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    summary: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    wide: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  computed: {
    isEmptyValue(): boolean {
      if (this.value === null || this.value === undefined) return true
      const text = String(this.value).trim()
      return text === '' || text === '-'
    },
  },
})
</script>

<style scoped>
.detail-info-item {
  position: relative;
  min-width: 0;
}

.detail-info-item--inline {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  align-items: center;
  column-gap: 12px;
  min-height: 32px;
  padding: 5px 0;
}

.detail-info-item--editable {
  grid-template-columns: 88px minmax(0, 1fr) 30px;
  column-gap: 8px;
}

.detail-info-item__label-wrap {
  display: flex;
  align-items: center;
  min-width: 0;
}

.detail-info-item__label {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.2;
}

.detail-info-item--summary .detail-info-item__label {
  display: block;
  margin-bottom: 4px;
  font-size: 0.72rem;
}

.detail-info-item__value {
  min-width: 0;
  overflow: hidden;
  color: #1f2937;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.35;
  word-break: break-word;
}

.detail-info-item--inline .detail-info-item__value {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.detail-info-item__link {
  color: #006fe6;
  cursor: pointer;
  font-weight: 750;
}

.detail-info-item__link:hover {
  color: #0057c2;
  text-decoration: underline;
}

.detail-info-item__action {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}

:deep(.detail-edit-action) {
  width: 24px;
  height: 24px;
  min-width: 24px;
  color: #94a3b8;
  opacity: 0.72;
  transition:
    color 0.16s ease,
    background-color 0.16s ease,
    opacity 0.16s ease;
}

:deep(.detail-edit-action:hover) {
  color: #2563eb;
  background: #eff6ff;
  opacity: 1;
}
</style>
